import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { AdminAPI, Hospital } from "@/app/apis/admin";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Account() {
  const navigate = useNavigate();
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHospitals();
  }, []);

  const fetchHospitals = async () => {
    try {
      setLoading(true);
      const data = await AdminAPI.getHospitals();
      setHospitals(data || []);
    } catch (err) {
      console.error("Failed to fetch hospitals:", err);
      toast.error("Failed to load hospitals data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Accounts</h1>
        <p className="text-muted-foreground">
          Hospitals, regions, and performance overview
        </p>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="p-4 text-center">Hospital</th>
                <th className="p-4 text-center">Region</th>
                <th className="p-4 text-center">Radiologists</th>
                <th className="p-4 text-center">Total Studies</th>
                <th className="p-4 text-center">Avg Sensitivity</th>
                <th className="p-4 text-center">Avg Specificity</th>
              </tr>
            </thead>

            <tbody>
              {hospitals.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-muted-foreground">
                    No hospitals found
                  </td>
                </tr>
              ) : (
                hospitals.map((hospital) => (
                  <tr
                    key={hospital.id}
                    className="border-b cursor-pointer hover:bg-muted/30 transition"
                    onClick={() =>
                      navigate(`/admin/accounts/${hospital.id}`)
                    }
                  >
                    <td className="p-4 font-medium">
                      {hospital.name}
                    </td>

                    <td className="p-4 text-muted-foreground">
                      {hospital.location || "—"}
                    </td>

                    <td className="p-4 text-center">
                      {hospital.radiologists ?? "—"}
                    </td>

                    <td className="p-4 text-center">
                      {hospital.totalStudies ?? "—"}
                    </td>

                    <td className="p-4 text-center">
                      {hospital.avgSensitivity !== null
                        ? `${(hospital.avgSensitivity * 100).toFixed(1)}%`
                        : "—"}
                    </td>

                    <td className="p-4 text-center">
                      {hospital.avgSpecificity !== null
                        ? `${(hospital.avgSpecificity * 100).toFixed(1)}%`
                        : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
